import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Save, Eye, EyeOff, Image, Upload, 
  AlertCircle, CheckCircle, Loader 
} from 'lucide-react';
import MDEditor from '@uiw/react-md-editor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PostEditor = () => {
  const navigate = useNavigate();
  const { postId } = useParams();
  const isEditing = Boolean(postId);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    banner_image: '',
    category_id: '',
    author: 'Resolv Digital',
    published: true
  });

  useEffect(() => {
    // Check if logged in
    const isAdmin = sessionStorage.getItem('isAdmin');
    if (isAdmin !== 'true') {
      navigate('/admin');
      return;
    }

    fetchCategories();
    if (isEditing) {
      fetchPost();
    }
  }, [navigate, isEditing, postId]);

  const getAuthHeaders = () => {
    const password = sessionStorage.getItem('adminPassword');
    return {
      'Authorization': 'Basic ' + btoa(`admin:${password}`),
      'Content-Type': 'application/json'
    };
  };

  const getUploadHeaders = () => {
    const password = sessionStorage.getItem('adminPassword');
    return {
      'Authorization': 'Basic ' + btoa(`admin:${password}`)
    };
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPost = async () => {
    setLoading(true);
    try {
      // Fetch post by ID directly
      const response = await fetch(`${API_URL}/api/blog/posts/id/${postId}`);
      if (response.ok) {
        const post = await response.json();
        setFormData({
          title: post.title,
          excerpt: post.excerpt,
          content: post.content,
          banner_image: post.banner_image || '',
          category_id: post.category_id || '',
          author: post.author,
          published: post.published
        });
      } else {
        setMessage({ type: 'error', text: 'Post not found' });
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      setMessage({ type: 'error', text: 'Error loading post' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContentChange = (value) => {
    setFormData(prev => ({
      ...prev,
      content: value || ''
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/blog/upload`, {
        method: 'POST',
        headers: getUploadHeaders(),
        body: uploadFormData
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          banner_image: data.url
        }));
        setMessage({ type: 'success', text: 'Image uploaded successfully' });
      } else {
        setMessage({ type: 'error', text: 'Failed to upload image' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage({ type: 'error', text: 'Error uploading image' });
    } finally {
      setUploading(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      setMessage({ type: 'error', text: 'Title is required' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    if (!formData.excerpt.trim()) {
      setMessage({ type: 'error', text: 'Excerpt is required' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }
    if (!formData.content.trim()) {
      setMessage({ type: 'error', text: 'Content is required' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    setSaving(true);

    try {
      const url = isEditing 
        ? `${API_URL}/api/blog/posts/${postId}` 
        : `${API_URL}/api/blog/posts`;
      
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          category_id: formData.category_id || null
        })
      });

      if (response.ok) {
        setMessage({ type: 'success', text: isEditing ? 'Post updated successfully' : 'Post created successfully' });
        setTimeout(() => {
          navigate('/admin/dashboard');
        }, 1500);
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to save post' });
      }
    } catch (error) {
      console.error('Save error:', error);
      setMessage({ type: 'error', text: 'Error saving post' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const bannerUrl = formData.banner_image?.startsWith('/') 
    ? `${API_URL}${formData.banner_image}` 
    : formData.banner_image;

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f3f4f6'
      }}>
        <Loader size={32} style={{ color: '#0D7377', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f3f4f6'
    }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <Link
              to="/admin/dashboard"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: '#6b7280',
                textDecoration: 'none',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.95rem'
              }}
            >
              <ArrowLeft size={18} /> Back
            </Link>
            <h1 style={{
              fontFamily: 'Montserrat, sans-serif',
              fontWeight: 700,
              fontSize: '1.25rem',
              color: '#111827'
            }}>
              {isEditing ? 'Edit Post' : 'New Post'}
            </h1>
          </div>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: saving ? '#9ca3af' : '#0D7377',
              color: '#ffffff',
              padding: '10px 20px',
              borderRadius: '8px',
              border: 'none',
              fontFamily: 'Lato, sans-serif',
              fontSize: '0.95rem',
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease'
            }}
            data-testid="save-post-button"
          >
            {saving ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={16} />}
            {saving ? 'Saving...' : (isEditing ? 'Update Post' : 'Publish Post')}
          </button>
        </div>
      </header>

      {/* Message */}
      {message.text && (
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '16px 24px 0'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: message.type === 'success' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(220, 53, 69, 0.1)',
            border: `1px solid ${message.type === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(220, 53, 69, 0.3)'}`,
            borderRadius: '8px',
            padding: '12px 16px'
          }}>
            {message.type === 'success' ? (
              <CheckCircle size={18} style={{ color: '#10b981' }} />
            ) : (
              <AlertCircle size={18} style={{ color: '#dc3545' }} />
            )}
            <span style={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '0.95rem',
              color: message.type === 'success' ? '#10b981' : '#dc3545'
            }}>{message.text}</span>
          </div>
        </div>
      )}

      {/* Main Form */}
      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '32px 24px'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 320px',
          gap: '32px'
        }}>
          {/* Main Content */}
          <div>
            {/* Title */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '8px'
              }}>
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter post title..."
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s ease',
                  color: '#111827',
                  backgroundColor: '#ffffff'
                }}
                onFocus={(e) => e.target.style.borderColor = '#0D7377'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                data-testid="post-title-input"
              />
            </div>

            {/* Excerpt */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '8px'
              }}>
                Excerpt * <span style={{ fontWeight: 400, color: '#9ca3af' }}>(Brief summary for listing pages)</span>
              </label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                placeholder="Write a brief summary of the post..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  transition: 'border-color 0.2s ease',
                  color: '#111827',
                  backgroundColor: '#ffffff'
                }}
                onFocus={(e) => e.target.style.borderColor = '#0D7377'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                data-testid="post-excerpt-input"
              />
            </div>

            {/* Content Editor */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '8px'
              }}>
                Content * <span style={{ fontWeight: 400, color: '#9ca3af' }}>(Supports Markdown and rich text paste)</span>
              </label>
              <div data-color-mode="light">
                <MDEditor
                  value={formData.content}
                  onChange={handleContentChange}
                  height={500}
                  preview="live"
                  hideToolbar={false}
                  enableScroll={true}
                  data-testid="post-content-editor"
                />
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div>
            {/* Publish Settings */}
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <h3 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 600,
                fontSize: '1rem',
                color: '#111827',
                marginBottom: '20px'
              }}>
                Publish Settings
              </h3>

              {/* Status */}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                marginBottom: '20px'
              }}>
                <input
                  type="checkbox"
                  name="published"
                  checked={formData.published}
                  onChange={handleChange}
                  style={{
                    width: '20px',
                    height: '20px',
                    accentColor: '#0D7377'
                  }}
                />
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.95rem',
                  color: '#374151'
                }}>
                  {formData.published ? <Eye size={18} /> : <EyeOff size={18} />}
                  {formData.published ? 'Published' : 'Draft'}
                </span>
              </label>

              {/* Category */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Category
                </label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.95rem',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer',
                    outline: 'none',
                    color: '#111827'
                  }}
                  data-testid="post-category-select"
                >
                  <option value="">No Category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Author */}
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '8px'
                }}>
                  Author
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.95rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                    color: '#111827',
                    backgroundColor: '#ffffff'
                  }}
                  data-testid="post-author-input"
                />
              </div>
            </div>

            {/* Banner Image */}
            <div style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              padding: '24px'
            }}>
              <h3 style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 600,
                fontSize: '1rem',
                color: '#111827',
                marginBottom: '20px'
              }}>
                Banner Image
              </h3>

              {/* Preview */}
              {formData.banner_image && (
                <div style={{
                  marginBottom: '16px',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  border: '1px solid #e5e7eb'
                }}>
                  <img
                    src={bannerUrl}
                    alt="Banner preview"
                    style={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'cover'
                    }}
                  />
                </div>
              )}

              {/* Upload Button */}
              <label style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '12px 16px',
                backgroundColor: uploading ? '#e5e7eb' : '#f3f4f6',
                border: '2px dashed #d1d5db',
                borderRadius: '8px',
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontFamily: 'Lato, sans-serif',
                fontSize: '0.9rem',
                color: '#6b7280',
                transition: 'all 0.2s ease',
                marginBottom: '12px'
              }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  style={{ display: 'none' }}
                  data-testid="banner-image-upload"
                />
                {uploading ? (
                  <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Upload size={18} />
                )}
                {uploading ? 'Uploading...' : 'Upload Image'}
              </label>

              {/* URL Input */}
              <div>
                <label style={{
                  display: 'block',
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '0.8rem',
                  color: '#9ca3af',
                  marginBottom: '6px'
                }}>
                  Or paste image URL:
                </label>
                <input
                  type="text"
                  name="banner_image"
                  value={formData.banner_image}
                  onChange={handleChange}
                  placeholder="https://..."
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.85rem',
                    outline: 'none',
                    boxSizing: 'border-box',
                    color: '#111827',
                    backgroundColor: '#ffffff'
                  }}
                  data-testid="banner-image-url-input"
                />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PostEditor;
